import { useTranslate } from "@tolgee/react";
import { ArrowDownToLine, ArrowUpRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { RefreshAssetsButton } from "#/components/refresh-assets-button";
import { Button } from "#/components/ui/button";
import { InfoTooltip } from "#/components/ui/info-tooltip";
import { DepositDialog } from "#/features/trade/components/order-form/deposit-dialog";
import { DepositWarningIndicator } from "#/features/trade/components/order-form/deposit-warning-indicator";
import { WithdrawDialog } from "#/features/trade/components/order-form/withdraw-dialog";
import { WithdrawStatusIndicator } from "#/features/trade/components/order-form/withdraw-status-indicator";
import { useHyperAccount } from "#/hooks/use-hyperliquid-account";
import {
	useDeposit,
	useExternalWithdraw,
	useTransferToWallet,
} from "#/hooks/use-trading-actions";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import { useWallet } from "#/hooks/use-wallet";
import { useWalletBalance } from "#/hooks/use-wallet-balance";
import { formatSize, formatUsd, toNumber } from "#/lib/formatters";

export function WalletOverview() {
	const { t } = useTranslate();
	const wallet = useWallet();
	const { balance: usdcBalance } = useWalletBalance();
	const { accountState } = useHyperAccount();
	const { isUnified, isDexAbstraction, spotUsdc, spotUsdcHold } =
		useUnifiedBalance();
	const depositAction = useDeposit();
	const transferToWalletAction = useTransferToWallet();
	const externalWithdrawAction = useExternalWithdraw();

	const margin =
		accountState?.marginSummary ?? accountState?.crossMarginSummary;
	const totalMarginUsed = toNumber(margin?.totalMarginUsed);
	const perpTradingBalance = accountState
		? toNumber(
				accountState.marginSummary?.accountValue ??
					accountState.crossMarginSummary.accountValue,
			)
		: 0;
	// See ACCOUNT.md → Balance display rules. Equity = positions MTM + free
	// spot. spot.hold is already inside perpTradingBalance for unified, so
	// subtracting yields the free portion only.
	const tradingBalance =
		isUnified || isDexAbstraction
			? perpTradingBalance + spotUsdc - spotUsdcHold
			: perpTradingBalance;
	const withdrawable =
		isUnified && spotUsdc > 0
			? Math.max(
					toNumber(accountState?.withdrawable),
					spotUsdc - totalMarginUsed,
				)
			: toNumber(accountState?.withdrawable);

	return (
		<div>
			<div className="flex items-center justify-between gap-4">
				<div className="flex items-center gap-2">
					<h2 className="text-body2-bold text-foreground">
						{t("portfolio.wallet")}
					</h2>
					<RefreshAssetsButton />
				</div>
				{wallet.address && (
					<Button
						variant="ghost"
						size="xs"
						onClick={() => {
							navigator.clipboard.writeText(wallet.address as string);
							toast.success(t("actions.copied"));
						}}
						className="text-muted-foreground"
					>
						<span>{wallet.displayAddress}</span>
						<Copy className="h-3 w-3" />
					</Button>
				)}
			</div>

			{/* repeat(5,1fr) = minmax(auto,1fr) — 칸은 기본 20%지만 내용이 길면 빈 트랙을 먹으며 늘어나 디바이더가 따라온다 (minmax(0,1fr)인 grid-cols-5는 내용을 잘라먹음) */}
			<div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-[repeat(5,1fr)] sm:gap-x-0 sm:divide-x sm:divide-border">
				<div className="sm:pr-6">
					<InfoTooltip
						content={t("account.wallet-usdc-tooltip")}
						className="block w-fit whitespace-nowrap text-label2 text-muted-foreground"
					>
						{t("portfolio.wallet-usdc")}
					</InfoTooltip>
					<p className="text-title2 text-foreground">
						{formatSize(usdcBalance, 2)}
					</p>
				</div>
				<div className="sm:pl-6 sm:pr-6">
					<InfoTooltip
						content={t("account.trading-account-tooltip")}
						className="block w-fit whitespace-nowrap text-label2 text-muted-foreground"
					>
						{t("account.trading-account")}
					</InfoTooltip>
					<p className="text-title2 text-foreground">
						{formatUsd(tradingBalance)}
					</p>
				</div>
				<div className="sm:pl-6">
					<p className="whitespace-nowrap text-label2 text-muted-foreground">
						{t("portfolio.free-collateral")}
					</p>
					<p className="text-title2 text-foreground">
						{formatUsd(withdrawable)}
					</p>
				</div>
			</div>

			<div className="mt-4 flex items-center gap-3">
				<DepositDialog
					onConfirm={(amount) => depositAction.mutateAsync(amount)}
				>
					<Button
						variant="default"
						size="sm"
						className="min-w-25 text-primary-foreground"
					>
						<ArrowDownToLine className="h-3.5 w-3.5" />
						{t("actions.deposit-to-trading")}
						<DepositWarningIndicator
							className="ml-0.5"
							statusClassName="text-primary-foreground"
						/>
					</Button>
				</DepositDialog>

				<WithdrawDialog
					onTransferToWallet={async (amount) => {
						await transferToWalletAction.mutateAsync(amount);
					}}
					onExternalWithdraw={async ({ amount, destination }) => {
						await externalWithdrawAction.mutateAsync({ amount, destination });
					}}
				>
					<Button
						variant="default"
						size="sm"
						className="min-w-25 text-primary-foreground"
					>
						<ArrowUpRight className="h-3.5 w-3.5" />
						{t("actions.withdraw")}
						<WithdrawStatusIndicator className="ml-0.5 text-primary-foreground" />
					</Button>
				</WithdrawDialog>
			</div>
		</div>
	);
}
